import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { AuthenticatedUser, Permission } from '@unifemga/shared';
import { hasPermission } from '@unifemga/shared';
import { ApiError, api, tokens } from './api';

interface LoginResult {
  accessToken: string;
  refreshToken: string;
  requiresTwoFactorEnrolment?: boolean;
  user: { isAdmin: boolean; mustChangePassword: boolean };
}

interface AuthContextValue {
  user: AuthenticatedUser | null;
  loading: boolean;
  signIn: (email: string, password: string, totpCode?: string) => Promise<LoginResult>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (permission: Permission | Permission[]) => boolean;
  /** True while an administrator is signed in but has not finished 2FA setup. */
  needsEnrolment: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    if (!tokens.access()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      setUser(await api.get<AuthenticatedUser>('/auth/me'));
    } catch {
      // A dead or revoked token must not leave a stale principal in memory.
      tokens.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  const signIn = useCallback(
    async (email: string, password: string, totpCode?: string): Promise<LoginResult> => {
      const result = await api.post<LoginResult>('/auth/login', {
        email,
        password,
        ...(totpCode ? { totpCode } : {}),
        rememberMe: true,
      });
      tokens.set(result.accessToken, result.refreshToken);
      await refreshUser();
      return result;
    },
    [refreshUser],
  );

  const signOut = useCallback(async () => {
    const refreshToken = tokens.refresh();
    try {
      if (refreshToken) await api.post('/auth/logout', { refreshToken });
    } catch {
      // Signing out locally must succeed even if the server call fails —
      // otherwise a network blip leaves someone unable to leave a session.
    }
    tokens.clear();
    setUser(null);
  }, []);

  const can = useCallback(
    (permission: Permission | Permission[]) =>
      user ? hasPermission(user.permissions, permission) : false,
    [user],
  );

  const value = useMemo(
    () => ({
      user,
      loading,
      signIn,
      signOut,
      refreshUser,
      can,
      needsEnrolment: user?.scope === 'enrolment_only',
    }),
    [user, loading, signIn, signOut, refreshUser, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>.');
  return context;
}

/**
 * Route guard.
 *
 * Sends an unauthenticated visitor to sign in, an admin mid-2FA-setup to
 * enrolment, and anyone lacking the required permission to their own home.
 * The permission check mirrors the server's — it is a usability measure, not
 * the security boundary, which lives in the API.
 */
export function RequireAuth({
  children,
  permission,
  requireAdmin,
  requireMember,
}: {
  children: ReactNode;
  permission?: Permission | Permission[];
  requireAdmin?: boolean;
  requireMember?: boolean;
}) {
  const { user, loading, can, needsEnrolment } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (needsEnrolment) return <Navigate to="/two-factor-setup" replace />;

  if (requireAdmin && !user.isAdmin) return <Navigate to="/portal" replace />;
  if (requireMember && !user.memberId) return <Navigate to="/admin" replace />;
  if (permission && !can(permission)) {
    return <Navigate to={user.isAdmin ? '/admin' : '/portal'} replace />;
  }

  return <>{children}</>;
}

export { ApiError };
